import type { IrrResult, Warning } from '@hjci-investorhub-v2/domain';
import type { ReferenceData } from '../../lib/api';

type ReviewKind = 'prices' | 'purchases' | 'transfers';
interface Explanation { title: string; why: string; next?: string; kind?: ReviewKind }
const explanations: Record<string, Explanation> = {
  UNKNOWN_PRICE_BASIS: {
    title: 'Price adjustment basis needs confirmation',
    why: 'Prices are available, but it is unconfirmed whether they already include splits or consolidations. Mixing adjusted prices with historical share quantities can misstate valuations and XIRR.',
    next: 'Check the provider history and dated corporate actions, then use Confirm price basis for the verified securities and date range.', kind: 'prices',
  },
  INVALID_TRANSFER_LOT: {
    title: 'Transfers need their source holdings checked',
    why: 'These movements cannot be matched to enough units in the source holding on their recorded date. Their effect on holdings and returns is unresolved.',
    next: 'Check the date, security, subfolio, available units and movement type. Network fees allocate automatically; other movements need their source purchase lot.', kind: 'transfers',
  },
  UNCLASSIFIED_TRANSFER: {
    title: 'Transfers need an accounting classification',
    why: 'The system cannot determine whether these movements are internal transfers, external transfers or network fees.',
    next: 'Check the source records and classify each movement before including it in returns.', kind: 'transfers',
  },
  INVALID_REVIEWED_ADJUSTMENT: {
    title: 'A reviewed adjustment conflicts with the holding',
    why: 'The approved adjustment cannot be applied to the current dated holding or overlaps a posted corporate action.',
    next: 'Check the original adjustment and subsequent changes. Technical details identify the conflict.', kind: 'purchases',
  },
  UNCLASSIFIED_LEGACY_ADJUSTMENT: {
    title: 'Historical adjustments need classification',
    why: 'A purchase contains nonpositive units or negative cost. It may represent an old adjustment rather than an ordinary purchase, so its accounting effect cannot be assumed.',
    next: 'Review the original notes and reconcile the adjustment with the appropriate dated corporate action or correction.', kind: 'purchases',
  },
  BTC_FEE_V1_COMPATIBILITY: {
    title: 'Bitcoin fees temporarily follow v1',
    why: 'BTC units are reduced and their AUD value is also charged as an investment cost. This matches the agreed v1 fee treatment, but counts the fee twice.',
    next: 'The previous v2 units-only treatment is the preferred economic treatment and may be restored after review.', kind: 'transfers',
  },
  BTC_FEE_PRICE_REQUIRED: {
    title: 'A Bitcoin fee-date price is missing',
    why: 'The temporary v1 fee cost needs the closing price on the fee date. An earlier price or zero cost is not substituted.',
    next: 'Import or review the price on the affected fee date.', kind: 'prices',
  },
  BTC_HISTORICAL_FEE_COMPATIBILITY: {
    title: 'Earlier Bitcoin fees omitted for v1 comparison',
    why: 'Report holdings and valuations omit BTC fees from before the selected period, matching v1 monthly reports. The actual ledger still deducts those fees.',
    next: 'Cumulative fee deductions are the preferred economic treatment and may be restored after review.', kind: 'transfers',
  },
  MISSING_PRICE: {
    title: 'A valuation price is missing',
    why: 'No price is available on or before an opening or closing valuation date.',
    next: 'Import prices covering the affected dates and check the security mapping.', kind: 'prices',
  },
  PREVIOUS_CLOSING_PRICE: {
    title: 'Using previous closing prices',
    why: 'The latest available closing prices are used for these valuation dates. They are within the report’s price-age limit; valuations and XIRR use these quotes.', kind: 'prices',
  },
  STALE_PRICE: {
    title: 'Prices older than the report’s age limit',
    why: 'These quotes exceed the price-age limit for this report. Calculations still use them.',
    next: 'Check the quote dates and refresh prices if newer observations should be available.', kind: 'prices',
  },
  STALE_OR_FILLED_PRICE: {
    title: 'Previously recorded closing prices',
    why: 'This saved calculation uses earlier quotes. The original messages in Technical details show their observation dates.', kind: 'prices',
  },
  PRICE_BASIS_CONVERTED: {
    title: 'Prices converted for posted corporate actions',
    why: 'The calculation converted a price to match the dated share quantities using posted actions. This note does not block the calculation.',
    next: 'Check Price sources and the posted corporate actions if you need to trace the conversion.', kind: 'prices',
  },
};
export interface WarningGroup extends Explanation { key: string; code: string; severity: Warning['severity']; findings: Warning[] }
export function groupWarnings(warnings: Warning[]): WarningGroup[] {
  const groups = new Map<string, WarningGroup>();
  for (const warning of warnings) {
    const key = `${warning.severity}:${warning.code}`;
    const explanation = explanations[warning.code] ?? {
      title: warning.code.toLowerCase().replaceAll('_', ' ').replace(/^./, c => c.toUpperCase()),
      why: 'A source record needs review. The exact check and original message are available in Technical details.',
      next: 'Review the affected source records before relying on the associated result.',
    };
    const group = groups.get(key) ?? { ...explanation, key, code: warning.code, severity: warning.severity, findings: [] };
    // Keep every finding, including separate valuation dates and subfolios for the same source.
    group.findings.push(warning); groups.set(key, group);
  }
  for (const group of groups.values()) {
    if (group.code !== 'PREVIOUS_CLOSING_PRICE') continue;
    const dates = [...new Set(group.findings.map(w => w.sourceDate))];
    if (dates.length === 1 && dates[0]) {
      const observed = new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(dates[0]));
      group.title = `Using previous closing price: ${observed}`;
    }
  }
  return [...groups.values()].sort((a, b) => Number(a.severity === 'warning') - Number(b.severity === 'warning') || a.title.localeCompare(b.title));
}
export function warningLabels(warning: Warning, reference?: ReferenceData) {
  const security = reference?.securities.find(s => s.id === warning.securityId);
  const subfolio = reference?.subfolios.find(s => s.id === warning.subfolioId);
  return { security: security?.symbol ?? security?.name ?? (warning.securityId ? 'Security unavailable' : 'Whole selection'), subfolio: subfolio?.name ?? (warning.subfolioId ? 'Subfolio unavailable' : '—') };
}
export function affectedScopes(group: WarningGroup, reference?: ReferenceData) {
  const scopes = new Map<string, { key: string; security: string; subfolio: string; warning: Warning; count: number; dates: string[]; sourceDates: string[] }>();
  for (const warning of group.findings) {
    const key = JSON.stringify([warning.securityId, warning.subfolioId]);
    const scope = scopes.get(key) ?? { key, ...warningLabels(warning, reference), warning, count: 0, dates: [], sourceDates: [] };
    scope.count++;
    if (warning.date && !scope.dates.includes(warning.date)) scope.dates.push(warning.date);
    if (warning.sourceDate && !scope.sourceDates.includes(warning.sourceDate)) scope.sourceDates.push(warning.sourceDate);
    scopes.set(key, scope);
  }
  return [...scopes.values()].map(s => ({ ...s, dates: s.dates.sort(), sourceDates: s.sourceDates.sort() })).sort((a, b) => a.security.localeCompare(b.security) || a.subfolio.localeCompare(b.subfolio));
}
export function reviewPath(kind: ReviewKind, warning?: Warning): string {
  const params = new URLSearchParams({ kind });
  if (warning?.securityId) params.set('securityId', warning.securityId);
  if (kind !== 'prices' && warning?.subfolioId) params.set('subfolioId', warning.subfolioId);
  return `/records?${params}`;
}
export function irrExplanation(reason: IrrResult['reason']): string | undefined {
  return {
    calculated: undefined,
    'missing-values': 'XIRR is unavailable because valuations or investment records are incomplete. Resolve the blocking issues below; the cashflows shown are not a complete basis for this return.',
    'no-opposing-cashflows': 'XIRR needs both money invested and money returned, including any closing valuation. This selection does not have both.',
    'same-day': 'XIRR needs cashflows on different dates. An annualised return cannot be calculated from a single day.',
    'multiple-sign-changes': 'These cashflows do not produce a single reliable XIRR. Multiple changes between inflows and outflows can produce ambiguous annualised returns.',
    nonconvergent: 'A stable XIRR could not be found for these dated cashflows. Review their amounts and dates.',
  }[reason];
}
